/**
 * Room — the ethical wall, demonstrated instead of claimed.
 *
 * Pick who you are. Tap an ask. Watch it happen, or watch it be refused in the
 * platform's own words. Then flip one permission on a custom role and watch the
 * same ask change its answer.
 *
 * WHY THIS SECTION EXISTS. Security, privilege and ethical walls are the top
 * barrier law firms report to adopting AI, and every document-management vendor
 * answers them with a feature bullet. The sentence that actually settles it is
 * that an ethical wall is only real if a system refuses the access — so the
 * page refuses it, in front of the visitor, from the shipped catalog.
 *
 * NOTHING HERE IS AUTHORED. Roles, permissions and verdicts come from
 * roomScenarios.ts (the two CI-gated mirrors); the refusal sentence comes from
 * the byte-for-byte mirror of the platform's own copy. The only strings written
 * for this page are the asks themselves and the framing around them.
 *
 * R8 — ROLES STAY PRIVATE IN A SHARED ROOM. The room shows other people
 * present, because that is what makes it a shared thread, but it never names
 * anyone else's role and never explains a refusal by who else is here. That is
 * the live product rule, mirrored, and it is asserted in tests/room.
 *
 * D8 — nothing classifies anything. The visitor states who they are by
 * clicking. No model call, no inference, and the section says plainly that it
 * is a demonstration.
 */

import React from 'react';
import styled from 'styled-components';
import KeepCase from '../common/KeepCase';
import {
  adminExcludedLabels,
  ROOM_ASKS,
  rolesInOrder,
  holds,
  allowedCount,
  permissionLabel,
  toggleableAskFor,
  type RoleKey,
  type RoomAsk,
} from './roomScenarios';
import { composePermissionRefusal } from '../../constants/permissionRefusalCopy.generated';

const Wrap = styled.section`
  padding: 4rem 1.5rem;
  background: var(--brand-surface);
  border-top: 1px solid var(--brand-line);
  border-bottom: 1px solid var(--brand-line);
`;

const Inner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Head = styled.h2`
  font-family: 'Satoshi', 'Inter', sans-serif;
  font-size: clamp(1.5rem, 3.6vw, 2.1rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--brand-text);
  margin: 0 0 0.6rem;
`;

const Lead = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
  max-width: 62ch;
  margin: 0 0 1.75rem;
`;

const Label = styled.div`
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  color: var(--brand-text-secondary);
  margin: 0 0 0.55rem;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Chip = styled.button<{ $on?: boolean }>`
  appearance: none;
  border: 1px solid ${(p) => (p.$on ? 'var(--brand-gold)' : 'var(--brand-line-strong)')};
  background: ${(p) => (p.$on ? 'var(--brand-gold)' : 'var(--brand-canvas)')};
  color: ${(p) => (p.$on ? 'var(--brand-charcoal)' : 'var(--brand-text)')};
  font: inherit;
  font-size: 0.9rem;
  padding: 0.6rem 1rem;
  min-height: 44px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
    border-color 160ms cubic-bezier(0.2, 0.8, 0.2, 1);

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 2px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: var(--brand-canvas);
  border: 1px solid var(--brand-line);
  border-radius: 12px;
  padding: 1.1rem 1.15rem;
`;

const AskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Ask = styled.button<{ $on?: boolean }>`
  appearance: none;
  text-align: left;
  border: 1px solid ${(p) => (p.$on ? 'var(--brand-gold)' : 'var(--brand-line)')};
  background: ${(p) => (p.$on ? 'rgba(198, 167, 94, 0.10)' : 'transparent')};
  color: var(--brand-text);
  font: inherit;
  font-size: 0.92rem;
  padding: 0.65rem 0.75rem;
  min-height: 44px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 2px;
  }
`;

const Mark = styled.span<{ $allowed: boolean }>`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  font-size: 0.7rem;
  color: #fff;
  background: ${(p) => (p.$allowed ? 'var(--brand-gold)' : 'var(--brand-danger)')};
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  min-height: 232px;
`;

const Bubble = styled.div<{ $me?: boolean }>`
  align-self: ${(p) => (p.$me ? 'flex-end' : 'flex-start')};
  max-width: 92%;
  background: ${(p) => (p.$me ? 'var(--brand-charcoal)' : 'var(--brand-surface)')};
  color: ${(p) => (p.$me ? 'var(--brand-canvas)' : 'var(--brand-text)')};
  border: 1px solid ${(p) => (p.$me ? 'transparent' : 'var(--brand-line)')};
  border-radius: 12px;
  padding: 0.7rem 0.85rem;
  font-size: 0.9rem;
  line-height: 1.55;
`;

const Who = styled.div`
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  opacity: 0.7;
  margin-bottom: 0.25rem;
`;

const Roster = styled.p`
  font-size: 0.8rem;
  color: var(--brand-text-secondary);
  margin: 0 0 0.85rem;
  padding-bottom: 0.7rem;
  border-bottom: 1px solid var(--brand-line);
`;

const Note = styled.p`
  font-size: 0.84rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
  margin: 1.1rem 0 0;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.9rem;
  padding-top: 0.85rem;
  border-top: 1px solid var(--brand-line);
  font-size: 0.86rem;
  color: var(--brand-text);
  cursor: pointer;
  min-height: 44px;

  input {
    width: 18px;
    height: 18px;
    accent-color: var(--brand-gold);
    cursor: pointer;
  }
`;

const Disclosure = styled.p`
  font-size: 0.78rem;
  color: var(--brand-text-secondary);
  margin: 1.5rem 0 0;
  font-style: italic;
`;

const Live = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

/** What an allowed ask looks like happening. Illustration, and labelled as one. */
const OUTCOMES: Readonly<Record<string, string>> = {
  'read-file': 'opened the matter file — 41 documents, indexed. ask me anything in it.',
  translate: 'translated, and delivered as PDF and Word. the original is untouched.',
  research: 'a cited memo, with the sources listed, as PDF and Word.',
  draft: 'drafted from your own past letters, flagged for your review before it goes anywhere.',
  browse: 'worked the portal in a real browser on your workspace computer. you watched it happen.',
  email: 'written and held — it sends when you authorize it, never before.',
  invite: 'invitation sent, with the role you chose.',
  billing: 'plan change previewed, with the prorated amount, before anything is charged.',
};

export interface RoomProps {
  /** Start on a role other than the default. Used by tests. */
  initialRole?: RoleKey;
}

export const Room: React.FC<RoomProps> = ({ initialRole = 'paralegal' }) => {
  const roles = React.useMemo(() => rolesInOrder(), []);
  const [roleKey, setRoleKey] = React.useState<RoleKey>(initialRole);
  const [askId, setAskId] = React.useState<string>(ROOM_ASKS[0].id);
  const [custom, setCustom] = React.useState(false);

  const role = roles.find((r) => r.key === roleKey) ?? roles[0];
  const ask: RoomAsk = ROOM_ASKS.find((a) => a.id === askId) ?? ROOM_ASKS[0];

  // The custom-role moment: one permission, switched on, nothing else.
  const toggleAsk = React.useMemo(() => toggleableAskFor(roleKey), [roleKey]);
  const extraGrants = React.useMemo(
    () => (custom && toggleAsk ? new Set([toggleAsk.permissionKey]) : new Set<string>()),
    [custom, toggleAsk],
  );

  // Switching role invalidates a custom grant built for the previous one.
  React.useEffect(() => setCustom(false), [roleKey]);

  const allowed = holds(roleKey, ask.permissionKey, extraGrants);
  const count = allowedCount(roleKey, extraGrants);

  const answer = allowed
    ? OUTCOMES[ask.id] ?? 'done.'
    : composePermissionRefusal({ activityLabel: ask.activityLabel, roleNames: [role.name] });

  return (
    <Wrap id="the-room">
      <Inner>
        <Head>the wall, and what happens when you reach it</Head>
        <Lead>
          permissions here are resolved for the person asking, on every single message. a role
          that does not include something is refused — not filtered afterwards, not quietly
          dropped. pick a role and try it.
        </Lead>

        <Label id="room-role-label">who are you in this room?</Label>
        <ChipRow role="group" aria-labelledby="room-role-label">
          {roles.map((r) => (
            <Chip
              key={r.key}
              type="button"
              $on={r.key === roleKey}
              aria-pressed={r.key === roleKey}
              onClick={() => setRoleKey(r.key)}
            >
              {r.name}
            </Chip>
          ))}
        </ChipRow>

        <Grid>
          <Panel>
            <Label id="room-ask-label">ask for something</Label>
            <AskList role="group" aria-labelledby="room-ask-label">
              {ROOM_ASKS.map((a) => {
                const ok = holds(roleKey, a.permissionKey, extraGrants);
                return (
                  <Ask
                    key={a.id}
                    type="button"
                    $on={a.id === askId}
                    aria-pressed={a.id === askId}
                    onClick={() => setAskId(a.id)}
                  >
                    <Mark $allowed={ok} aria-hidden="true">{ok ? '✓' : '✕'}</Mark>
                    <span>{a.label}</span>
                  </Ask>
                );
              })}
            </AskList>

            {toggleAsk && (
              <Toggle>
                <input
                  type="checkbox"
                  checked={custom}
                  onChange={(e) => setCustom(e.target.checked)}
                />
                <span>
                  make a custom role: add <strong>{permissionLabel(toggleAsk.permissionKey)}</strong>
                </span>
              </Toggle>
            )}
          </Panel>

          <Panel>
            {/*
              The roster names presence, never anyone else's role (R8). It is
              the same rule the live product applies when it composes a group
              thread for the model.
            */}
            <Roster>
              in this thread: you, a teammate, and your client. <KeepCase>Prest0n</KeepCase> is
              here too.
            </Roster>

            <Thread>
              <Bubble $me>
                <Who>you — {role.name}</Who>
                {ask.label}
              </Bubble>
              <Bubble>
                <Who><KeepCase>Prest0n</KeepCase></Who>
                {answer}
              </Bubble>
            </Thread>

            <Note>
              {role.derived ? (
                <>
                  {role.name === 'Owner' ? (
                    <>an <strong>Owner</strong> holds every permission in the workspace. </>
                  ) : (
                    <>
                      an <strong>Admin</strong> runs the workspace day to day and holds everything
                      except what an owner keeps: {adminExcludedLabels().join(', ')}.{' '}
                    </>
                  )}
                  this is a rule the platform applies, not a list someone typed.
                </>
              ) : (
                <>
                  <strong>{role.name}</strong> — {role.description}
                </>
              )}{' '}
              of the eight asks here, this role can run <strong>{count}</strong>.
            </Note>

            <Live aria-live="polite">
              {`${role.name}: ${ask.label} — ${allowed ? 'allowed' : 'refused'}.`}
            </Live>
          </Panel>
        </Grid>

        <Disclosure>
          a demonstration, running on the real permission catalog and the real refusal wording
          this platform uses — not a live workspace, and nothing you tap here is sent anywhere.
        </Disclosure>
      </Inner>
    </Wrap>
  );
};

export default Room;
